import React from 'react';
import { ChevronLeft, Circle, CircleDot, Minus, Plus, X } from 'lucide-react';
import { DateField } from '../../../components/ui/DateTimeField';
import { useT } from '../../../i18n/admin';

/**
 * SAM-944 / SAM-1171 — New pass at 375 px, steps 1 to 3 (canvas v256: Type x0, Access x455, Guests x910).
 * One question per screen: header with back and close, a 7-segment progress bar, the content, and the
 * primary action fixed at the bottom. The state and the rules stay in AccessForm; these are the pieces.
 */

export const PASS_STEPS = 7;

export const PhoneStepShell: React.FC<{
  step: number;
  onBack?: () => void;
  onClose: () => void;
  onContinue?: () => void;
  continueDisabled?: boolean;
  continueLabel?: string;
  loading?: boolean;
  children: React.ReactNode;
}> = ({ step, onBack, onClose, onContinue, continueDisabled, continueLabel, loading, children }) => {
  const { t } = useT();
  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <header className="flex h-14 shrink-0 items-center justify-between border-0 border-b border-solid border-[var(--line)] pl-1 pr-2">
        <div className={`flex items-center gap-1 ${onBack ? '' : 'pl-3'}`}>
          {onBack && (
            <button type="button" aria-label={t('common.back')} onClick={onBack} className="flex h-11 w-11 items-center justify-center border-0 bg-transparent text-[var(--ink-900)]">
              <ChevronLeft size={22} aria-hidden />
            </button>
          )}
          <span className="text-[17px] font-bold text-[var(--ink-900)]">{t('booking.phone.title')}</span>
        </div>
        <button type="button" aria-label={t('booking.phone.close')} onClick={onClose} className="flex h-11 w-11 items-center justify-center border-0 bg-transparent text-[var(--ink-900)]">
          <X size={22} aria-hidden />
        </button>
      </header>
      <div className="shrink-0 px-4 pt-3">
        <div
          role="progressbar"
          aria-label={t('booking.phone.stepOf', { n: step, total: PASS_STEPS })}
          aria-valuemin={1}
          aria-valuemax={PASS_STEPS}
          aria-valuenow={step}
          className="h-1 rounded-sm bg-[var(--line)]"
        >
          <div className="h-1 rounded-sm bg-[var(--tenant-primary,#005488)]" style={{ width: `${(step / PASS_STEPS) * 100}%` }} />
        </div>
      </div>
      <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      {onContinue && (
        <div className="shrink-0 bg-white px-[22px] py-4">
          <button
            type="button"
            onClick={onContinue}
            disabled={continueDisabled || loading}
            className="h-[57px] w-full cursor-pointer rounded-xl border-0 bg-[var(--tenant-primary,#005488)] text-lg font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {continueLabel ?? t('common.continue')}
          </button>
        </div>
      )}
    </div>
  );
};

export const PhoneTypeStep: React.FC<{
  options: Array<{ label: string; value: string; disabled?: boolean }>;
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div role="radiogroup" className="flex flex-col p-4">
    {options.map((o) => {
      const on = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={on}
          aria-disabled={o.disabled || undefined}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
          className={`flex min-h-[56px] w-full cursor-pointer items-center justify-between border-0 border-b border-solid border-[var(--line)] bg-transparent px-0 py-3 text-left text-lg font-bold text-[var(--ink-900)] disabled:cursor-not-allowed ${o.disabled ? 'opacity-40' : ''}`}
        >
          {o.label}
          {on
            ? <CircleDot size={22} className="text-[var(--tenant-primary,#005488)]" aria-hidden />
            : <Circle size={22} className="text-[var(--ink-400)]" aria-hidden />}
        </button>
      );
    })}
  </div>
);

const pill = (on: boolean) =>
  `h-11 cursor-pointer rounded-full border-0 px-5 text-sm font-bold ${on ? 'bg-[var(--tenant-primary,#005488)] text-white' : 'bg-[var(--line)] text-[var(--ink-900)]'} disabled:cursor-not-allowed disabled:opacity-50`;

/** A local calendar day as YYYY-MM-DD, n days from today. */
export const dayFromToday = (n: number, now = new Date()): string => {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
  const p = (v: number) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export interface PhoneDateValue {
  mode: 'one' | 'multiple';
  date: string;
  from: string;
  to: string;
}

/** How long? + What date? — the date rows of the Access step. */
export const PhoneDateRows: React.FC<{
  value: PhoneDateValue;
  onChange: (next: PhoneDateValue) => void;
  multipleDisabled?: boolean;
  restrictionReason?: string;
}> = ({ value, onChange, multipleDisabled, restrictionReason }) => {
  const { t, lang } = useT();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const today = dayFromToday(0);
  const tomorrow = dayFromToday(1);
  const short = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  };
  const more = value.date !== today && value.date !== tomorrow;
  const [calendar, setCalendar] = React.useState(more && Boolean(value.date));
  const card = (on: boolean) =>
    `flex min-h-[64px] flex-1 cursor-pointer flex-col items-start gap-1 whitespace-nowrap rounded-[10px] border-2 border-solid p-2.5 text-left ${on ? 'border-[var(--tenant-primary,#005488)] bg-[color-mix(in_srgb,var(--line)_60%,transparent)]' : 'border-transparent bg-[color-mix(in_srgb,var(--line)_30%,transparent)]'}`;

  return (
    <>
      <div className="mt-2 text-2xl font-bold text-[var(--ink-900)]">{t('booking.access.howLong')}</div>
      <div className="flex gap-2">
        <button type="button" aria-pressed={value.mode === 'one'} className={pill(value.mode === 'one')} onClick={() => onChange({ ...value, mode: 'one' })}>
          {t('booking.howLong.oneDay')}
        </button>
        <button
          type="button"
          aria-pressed={value.mode === 'multiple'}
          disabled={multipleDisabled}
          className={pill(value.mode === 'multiple')}
          onClick={() => !multipleDisabled && onChange({ ...value, mode: 'multiple' })}
        >
          {t('booking.howLong.multipleDays')}
        </button>
      </div>
      {multipleDisabled && restrictionReason && <div className="text-[13px] text-[var(--ink-500)]">{restrictionReason}</div>}

      <div className="mt-2 text-[22px] font-bold text-[var(--ink-900)]">{t('booking.phone.whatDate')}</div>
      {value.mode === 'one' ? (
        <>
          <div className="flex gap-2">
            <button type="button" aria-pressed={value.date === today} className={card(value.date === today)} onClick={() => { setCalendar(false); onChange({ ...value, date: today }); }}>
              <span className="text-[15px] font-bold text-[var(--ink-900)]">{t('booking.howLong.today')}</span>
              <span className="text-[13px] text-[var(--ink-500)]">{short(today)}</span>
            </button>
            <button type="button" aria-pressed={value.date === tomorrow} className={card(value.date === tomorrow)} onClick={() => { setCalendar(false); onChange({ ...value, date: tomorrow }); }}>
              <span className="text-[15px] font-bold text-[var(--ink-900)]">{t('booking.phone.tomorrow')}</span>
              <span className="text-[13px] text-[var(--ink-500)]">{short(tomorrow)}</span>
            </button>
            <button type="button" aria-pressed={calendar || more} className={card(calendar || more)} onClick={() => setCalendar(true)}>
              <span className="text-[15px] font-bold text-[var(--ink-900)]">{t('booking.phone.moreDates')}</span>
              <span className="text-[13px] text-[var(--ink-500)]">{more && value.date ? short(value.date) : t('booking.phone.calendar')}</span>
            </button>
          </div>
          {(calendar || more) && (
            <DateField
              ariaLabel={t('booking.phone.moreDates')}
              value={more ? value.date : ''}
              min={today}
              placeholder={t('booking.phone.pickDay')}
              onChange={(date) => onChange({ ...value, date })}
            />
          )}
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <DateField
            label={t('booking.phone.from')}
            ariaLabel={t('booking.phone.from')}
            value={value.from}
            min={today}
            placeholder={t('booking.phone.pickDay')}
            onChange={(from) => onChange({ ...value, from, to: value.to && value.to < from ? from : value.to })}
          />
          <DateField
            label={t('booking.phone.to')}
            ariaLabel={t('booking.phone.to')}
            value={value.to}
            min={value.from || today}
            placeholder={t('booking.phone.pickDay')}
            onChange={(to) => onChange({ ...value, to })}
          />
        </div>
      )}
    </>
  );
};

const Stepper: React.FC<{
  title: string; hint: string; value: number; min: number; max?: number; onChange: (n: number) => void;
}> = ({ title, hint, value, min, max, onChange }) => {
  const { t } = useT();
  const btn = 'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-[#F3F3F3] text-black disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="text-base font-bold text-[var(--ink-900)]">{title}</div>
        <div className="mt-1 text-[13px] text-[var(--ink-500)]">{hint}</div>
      </div>
      <div className="flex items-center gap-4">
        <button type="button" className={btn} disabled={value <= min} aria-label={t('booking.phone.fewer', { what: title })} onClick={() => onChange(value - 1)}>
          <Minus size={14} strokeWidth={2.6} aria-hidden />
        </button>
        <span aria-live="polite" className="min-w-4 text-center text-lg font-bold">{value}</span>
        <button type="button" className={btn} disabled={max !== undefined && value >= max} aria-label={t('booking.phone.more', { what: title })} onClick={() => onChange(value + 1)}>
          <Plus size={14} strokeWidth={2.6} aria-hidden />
        </button>
      </div>
    </div>
  );
};

/** How many guests? — adults and children, no total (decision of 28 Oct). */
export const PhoneGuestsStep: React.FC<{
  adults: number;
  children: number;
  onAdults: (n: number) => void;
  onChildren: (n: number) => void;
  maxAdults?: number;
  maxChildren?: number;
  maxTotal?: number;
  error?: string;
}> = ({ adults, children, onAdults, onChildren, maxAdults, maxChildren, maxTotal, error }) => {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="text-[22px] font-bold text-[var(--ink-900)]">{t('booking.phone.howMany')}</div>
      {maxTotal !== undefined && <div className="text-[15px] text-[var(--ink-500)]">{t('booking.phone.upTo', { n: maxTotal })}</div>}
      <Stepper title={t('booking.howLong.adults')} hint={t('booking.phone.adultsHint')} value={adults} min={1} max={maxAdults} onChange={onAdults} />
      <div className="h-px bg-[var(--line)]" />
      <Stepper title={t('booking.howLong.children')} hint={t('booking.howLong.childrenHint')} value={children} min={0} max={maxChildren} onChange={onChildren} />
      {error && <div role="alert" className="text-sm text-[var(--bad-fg,#b42318)]">{error}</div>}
    </div>
  );
};

/** The local calendar day (YYYY-MM-DD) of an ISO instant, '' if it is missing or malformed. */
export const dayOfIso = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (v: number) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Start of that local day as an ISO instant — the shape HowLongControl has always produced. */
export const isoOfDay = (isoDay: string): string => {
  const [y, m, d] = isoDay.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};
